var Model = require('js/lib/model');
var _ = require('underscore');
var Api = require('js/api');
var Promise = require('js/vendor/promise');
var config = require('js/config');

/**
 * Context
 * @exports Context
 * @constructor
 * @alias module:models/context
 */
var Context = Model.extend({
  urlRoot: config.build.apiPath + '/api/context',
  sessionUrlRoot: config.build.apiPath + '/session',
  oauthSessionUrlRoot: config.build.apiPath + '/session/oauth',
  userGroupSubscriptionsUrlRoot: config.build.apiPath + '/api/user-group-subscriptions',
  defaults: {
    context: {
      isActive: false,
      user: {
        roles: [], permissions: [], groups: [], isCurrentGroupMember: false,
      },
    },
  },
  FOLLOW_TYPES: {
    USER: 'user',
    POST: 'post',
    GROUP: 'group',
  },
  signupContext: {
    element: 'UNTRACKED',
    section: 'UNTRACKED',
  },
});

/**
 * Parse attributes
 */
Context.prototype.parse = function(attributes) {
  // Parse entity json data
  if (typeof attributes.entity === 'string') {
    attributes.entity = JSON.parse(attributes.entity);
  }

  // Parse search json data
  if (typeof attributes.search === 'string') {
    attributes.search = JSON.parse(attributes.search);
  }

  if (attributes && attributes.context) {
    // fetching followers and followings
    if (attributes.context.user && attributes.context.user.groups.length) {
      attributes.context.user.groups = attributes.context.user.groups
        .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1);
    }
    if (attributes.context.locale && attributes.context.locale.split('_').length === 2) {
      attributes.context.lang = attributes.context.locale.split('_')[0];
    }
  }

  return attributes;
};

/**
 * Store the last updated context id
 */
Context.prototype.lastId = null;

// TODO: Need to abort old request when we make a new one as we don t care about it anymore

/**
 * Load data for the given context if the id isn t changing
 */
Context.prototype.update = function(id, force) {
  // No point doing anything if id isn t changing
  if (id === this.lastId && !force) {
    return Promise.fulfilled(this.attributes);
  }

  // Remember last updated ID
  this.lastId = id;

  return this.desc('url', id)
    .fetch({
      success: function(model, resp) { // Dirty fix, we need the fetch to replace existing attributes
        for (var attribute in model.attributes) {
          if (model.attributes.hasOwnProperty(attribute) && !resp[attribute]) {
            model.unset(attribute, { silent: true });
          }
        }
      },
      breakOnError: true,
    });
};

/*
 * Force context update using current Id
 */
Context.prototype.forceUpdate = function() {
  return this.update(this.lastId, true);
};

/**
 * Is current context a group context
 * @return {Boolean}
 */
Context.prototype.isGroup = function() {
  return this.has('group');
};

/**
 * Is current context a user context
 * @return {Boolean}
 */
Context.prototype.isUser = function() {
  return this.has('user');
};

/**
 * Return the current context  object
 * @return {Object} The context attributes object
 */
Context.prototype.getContext = function() {
  return this.get('context');
};

/**
 * Is this an 'active' context? Ie. is the session logged in
 * @return {Boolean}
 */
Context.prototype.isActive = function() {
  var contextData = this.getContext();

  return contextData && contextData.isActive || false;
};

/**
 * Return the current context type as a string
 * @return {String} The context type
 */
Context.prototype.getType = function() {
  if (this.isGroup()) {
    return 'community';
  } else if (this.isUser()) {
    return 'user';
  }
  return 'other';
};

/**
 * Return the current resolved group object
 * @return {Object} The resolved group attributes object
 */
Context.prototype.getGroup = function() {
  return this.isGroup() && this.get('group');
};

/**
 * Return the current resolved user object
 * @return {Object} The resolved user attributes object
 */
Context.prototype.getUser = function() {
  return this.isUser() && this.attributes.user;
};

/**
 * Is the current user a member of given group?
 * @param  {String} groupCode The group code to check for membership
 * @return {Boolean}
 */
Context.prototype.userIsMemberOfGroup = function(groupCode) {
  var userGroups = this.getContext().user.groups;

  for (var i = 0, numGroups = userGroups.length; i < numGroups; i++) {
    if (userGroups[i].groupCode === groupCode) {
      return true;
    }
  }

  return false;
};

/**
 * Is the current context user a member of the *current* group
 * @return {[type]} [description]
 */
Context.prototype.userIsMemberOfCurrentGroup = function() {
  return this.isGroup() && this.userIsMemberOfGroup(this.getGroup().groupCode);
};

/**
 * Check if context user has a specific role
 * @param  {String} askedRole Name of role
 */
Context.prototype.userHasRole = function(askedRole) {
  return _.indexOf(this.getContext().user.roles, askedRole) !== -1;
};

/**
 * Check if context user has a specific permission
 * @param  {String} askedPerm Name of permission
 */
Context.prototype.userHasPermission = function(askedPerm) {
  return _.indexOf(this.getContext().user.permissions, askedPerm) !== -1;
};

/**
 * Check if context user has specific permissions
 * @param  {Array} askedPerms Array of names of permissions
 */
Context.prototype.userHasPermissions = function(askedPerms) {
  return _.every(askedPerms, this.userHasPermission, this);
};

/**
 * Log current session in using given data (and forces context update)
 */
Context.prototype.login = function(username, password) {
  var data = {
    username,
    password,
  };

  // Whoops, already logged in!
  if (this.getContext().user.username) {
    return Promise.broken('Already logged in');
  }

  // Send API request
  return Api.post(this.sessionUrlRoot, { data })
    .then(function() {
      return this.forceUpdate();
    }, this);
};

/**
 * Log current session in (via facebook) using given data (and forces context update)
 */
Context.prototype.loginWithFacebook = function(accessToken, facebookUserId, userData) {
  var data = {
    accessToken,
    facebookUserId,
    ...userData,
  };

  // Send API request
  return Api.post(this.oauthSessionUrlRoot, { data })
    .then(this.forceUpdate, this);
};

Context.prototype.loginWithGoogle = function(accessToken, googleUserId, userData) {
  var data = {
    accessToken,
    googleUserId,
    ...userData,
  };

  // Send API request
  return Api.post(this.oauthSessionUrlRoot, { data })
    .then(this.forceUpdate, this);
};


/**
 * Logout current session (and forces context update)
 */
Context.prototype.logout = function(silent) {
  return Api.delete(this.sessionUrlRoot).then(function() {
    this.invalidate();

    var contextData = this.getContext();
    contextData.isActive = false;
    this.set('context', contextData);

    if (!silent) {
      return this.forceUpdate();
    }
  }, this);
};

/**
 * This invalidates the current context by changing the lastId, forcing a refresh
 */
Context.prototype.invalidate = function() {
  this.lastId = 0;
};

/**
 * Join active user to a group by given ID TODO: Accept group code to be consistent
 * @param  {Number} groupId ID of group
 */
Context.prototype.joinUserToGroup = function(groupId, silent) {
  return Api.post(this.userGroupSubscriptionsUrlRoot, {
    data: {
      UserId: this.getContext().user.id,
      GroupId: groupId,
    },
  }).then(function() {
    if (!silent) {
      this.forceUpdate();
      this.trigger('updatedGroups');
    }
  }, this);
};

/**
 * Remove active user from a group given ID TODO: Accept group code to be consistent
 * @param  {Number} groupId ID of group
 */
Context.prototype.removeUserFromGroup = function(groupId) {
  return Api.delete(this.userGroupSubscriptionsUrlRoot, {
    data: {
      UserId: this.getContext().user.id,
      GroupId: groupId,
    },
  }).then(function() {
    this.forceUpdate();
    this.trigger('updatedGroups');
  }, this);
};

/**
 * Update the current context user language
 */
Context.prototype.updateUserLang = function(lang) {
  return Api.put(config.build.apiPath + '/api/account/language', {
    data: {
      UserId: this.getContext().user.id,
      lang,
    },
  });
};

/**
 * Resend email verification email
 */
Context.prototype.resendVerificationLink = function() {
  return Api.post(config.build.apiPath + '/private/signup/resend-verification-link');
};

/**
 * Return the locale of the current session
 */
Context.prototype.getSessionLocale = function() {
  return this.getContext().locale;
};

/**
 * Return the language of the current session
 */
Context.prototype.getSessionLang = function() {
  const locale = this.getSessionLocale();

  if (!locale) {
    return 'en';
  }

  const localeParts = locale.split('_');

  return localeParts[0];
};

/**
 * Follow something
 * @param {Number|Number[]} ids Single or multiple ids
 */
Context.prototype.follow = function(ids) {
  return Api.post(
    config.build.apiPath + '/api/follow/group',
    { data: { UserId: this.getContext().user.id, GroupId: ids } },
  ).then(() => Promise.fulfilled(this.attributes), this);
};

/**
 * Unfollow something
 * @param {String} followType See FOLLOW_TYPES
 * @param {Number|Number[]} ids Single or multiple ids
 */
Context.prototype.unfollow = function(followType, ids, silent) {
  // Ensure array
  if (typeof ids === 'string') {
    ids = [ids];
  }

  return Api.delete(config.build.apiPath + '/api/follow/' + followType, {
    data: { Ids: ids },
  }).then(function() {
    if (!silent) {
      this.forceUpdate();
    }
  }, this);
};

/*
 * Get an set the signup context (for analytics)
 */
Context.prototype.getSignupContext = function() {
  return this.signupContext;
};
Context.prototype.setSignupContext = function(newContext) {
  return this.signupContext = newContext;
};

Context.prototype.fetchSessionInfo = function() {
  return Api.get(`${config.build.apiPath}/session/info`);
};

module.exports = Context;



// WEBPACK FOOTER //
// ./app/assets/js/models/context.js